/**
 * paywall-signal — the ONE way any client code asks the shell to show the
 * humane paywall (UpgradeRequiredModal).
 *
 * Two sources fire it:
 *   1. HTTP: a 402 PLATFORM_SUBSCRIPTION_REQUIRED from the hub or a UI route
 *      (fetchLocal / fetchInference in api.ts). `reason: 'trial_expired'` when
 *      the account's free trial has ended.
 *   2. WS: a chat `response` carrying `usageLimit` — the hub refused the turn
 *      because the free trial ended or a free-tier allowance (messages /
 *      images / videos) ran out (hub spec 2026-09-26-free-tier-build-design.md).
 *
 * Loop guard: never fires on the pay pages themselves.
 * Lives in its own module (not api.ts) so the WS client can use it without
 * importing the whole API client.
 */

export const UPGRADE_REQUIRED_EVENT = 'ctrl-a:upgrade-required';

export type UpgradeReason = 'subscription_required' | 'trial_expired' | 'plan_ended' | 'limit_reached';

export interface UpgradeRequiredDetail {
  code: string;
  path: string;
  reason: UpgradeReason;
  /** Server-authored, human explanation (what ran out / when the trial ended). */
  message?: string;
  resource?: 'messages' | 'images' | 'videos';
}

/** The `usageLimit` object the hub puts on WS/REST chat responses. */
export interface UsageLimitSignal {
  code: 'FREE_TRIAL_EXPIRED' | 'FREE_TRIAL_LIMIT_REACHED' | 'FREE_TRIAL_NOT_INCLUDED' | 'PLAN_ENDED';
  resource?: 'messages' | 'images' | 'videos';
  upgradeUrl: string;
}

const PAY_PAGES = new Set(['/upgrade', '/settings/billing']);

export function signalUpgradeRequired(detail: Omit<UpgradeRequiredDetail, 'path'>): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (PAY_PAGES.has(path)) return; // loop guard
  window.dispatchEvent(new CustomEvent<UpgradeRequiredDetail>(UPGRADE_REQUIRED_EVENT, { detail: { ...detail, path } }));
}

/** HTTP: fire only on 402 PLATFORM_SUBSCRIPTION_REQUIRED (never on 401). */
export function signalUpgradeRequiredIfNeeded(
  status: number,
  code: string | undefined,
  body?: { reason?: string; error?: string },
): void {
  if (status !== 402 || code !== 'PLATFORM_SUBSCRIPTION_REQUIRED') return;
  const reason: UpgradeReason =
    body?.reason === 'trial_expired' ? 'trial_expired'
    : body?.reason === 'plan_ended' ? 'plan_ended'
    : 'subscription_required';
  signalUpgradeRequired({
    code,
    reason,
    message: reason === 'subscription_required' ? undefined : body?.error,
  });
}

/** WS/REST chat: fire when the hub attached a free-tier `usageLimit`. */
export function signalUsageLimit(usageLimit: UsageLimitSignal | undefined, message?: string): void {
  if (!usageLimit) return;
  signalUpgradeRequired({
    code: usageLimit.code,
    // PLAN_ENDED (hub re-gate 2026-09-26): a lapsed payer's turn was refused —
    // show "your plan has ended", never trial copy.
    reason: usageLimit.code === 'PLAN_ENDED' ? 'plan_ended'
      : usageLimit.code === 'FREE_TRIAL_EXPIRED' ? 'trial_expired'
      : 'limit_reached',
    resource: usageLimit.resource,
    message,
  });
}
